/**
 * @file       secureLayout component
 * @version    2.0.0
 * @author     Mehran Majidi
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or 
 *             altered in anyway without expressed written permission from 
 *             MindShare HDV LLC.
 */

import { CommonModule } from '@angular/common';

//import { NgModule } from '@angular/core';
//import { FormsModule } from '@angular/forms';
//import { ReactiveFormsModule } from '@angular/forms';

//import { MatSidenavModule } from '@angular/material/sidenav';
//import { MatListModule } from '@angular/material/list';


//import { MatButtonModule } from '@angular/material/button';
//import { MatIconModule } from '@angular/material/icon';
//import { MatToolbarModule } from '@angular/material/toolbar';

import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import {
    Component, OnDestroy, OnInit, ChangeDetectionStrategy
} from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';

//import { Inject, ViewChild, ElementRef } from '@angular/core';


//import { Chart } from 'chart.js';
import { NavigationEnd, Router, RouterModule } from
    '@angular/router';
import { MatDialogModule, MatDialog, MatDialogRef } from
         '@angular/material/dialog';
import { SecureHeaderComponent } from 
    '@app/secure/core/layout/secureHeader.component';
import { IdleTimeoutComponent } from 
    '@app/secure/core/timeout/idleTimeout.component';
import { AuthService } from '@app/service/auth.service';
import { LOGOUT_REASON } from '@app/constants/Logout.constant';

@Component
({
    selector: 'app-secure-layout',
    templateUrl: './secureLayout.component.html',
    styleUrls: ['./secureLayout.component.scss'],
    imports:
    [
        CommonModule,
        RouterModule,
        MatDialogModule,
        SecureHeaderComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true
})

class SecureLayoutComponent implements OnInit, OnDestroy
{
    idleState:string = 'Not started.';
    timedOut:boolean = false;
    lastPing?: Date = null;

    private readonly sessionIdleLimitSec:number = 3600;
    private readonly warningWindowSec:number = 10;
    private idleStartSec:number = 3590;
    private timeoutPeriodSec:number = 10;
    private idleSubscriptions: Subscription;
    private dialogRef : MatDialogRef<IdleTimeoutComponent>;

    public countdown: number;
    
    /**
     * @desc   - component constructor
     * @access - public
     * @param  - 
     * @return - 
     */            
    constructor
    (
        private authService: AuthService,
        private dialog: MatDialog,
//        private keepalive: Keepalive,
        private idle: Idle,
        private router: Router,
        private liveAnnouncer: LiveAnnouncer
    )
    {
        this.idleSubscriptions = new Subscription();
        this.dialogRef = null;

        this.timeoutPeriodSec = this.warningWindowSec;
        this.idleStartSec = this.sessionIdleLimitSec - this.warningWindowSec;
        this.countdown = this.timeoutPeriodSec;
    }

    /**
     * @desc   - angular initializer
     * @access - public
     * @param  - 
     * @return - 
     */            
    ngOnInit() 
    {
        this.configureIdleWatcher();
        this.configureRouteAnnouncements();
    }

    /**
     * @desc   - angular destroy lifecycle
     * @access - public
     * @param  -
     * @return -
     */
    ngOnDestroy()
    {
        if (this.dialogRef)
        {
            this.dialogRef.close();
            this.dialogRef = null;
        }

        this.idle.stop();
        this.idleSubscriptions.unsubscribe();
    }

    /**
     * @desc Announce page name on NavigationEnd for screen readers
     * @access private
     * @param -
     * @return -
     */
    private configureRouteAnnouncements()
    {
        this.idleSubscriptions.add(
            this.router.events.pipe(
                filter((event): event is NavigationEnd =>
                    event instanceof NavigationEnd)
            ).subscribe((event: NavigationEnd) =>
            {
                this.announceCurrentPage(
                    event.urlAfterRedirects);
            })
        );
        this.announceCurrentPage(this.router.url);
    }

    /**
     * @desc Announce a short page name from route data or URL
     * @access private
     * @param url : navigated URL after redirects
     * @return -
     */
    private announceCurrentPage
    (
        url: string
    )
    {
        let pageName;

        pageName = this.resolvePageName(url);
        this.liveAnnouncer.announce(pageName);
    }

    /**
     * @desc Resolve page name from data.title or last URL segment
     * @access private
     * @param url : navigated URL after redirects
     * @return short page name for announcement
     */
    private resolvePageName
    (
        url: string
    ): string
    {
        let result;
        let title;
        let path;
        let segments;
        let segment;

        title = this.readDeepestRouteTitle();
        path = (url || '').split('?')[0].split('#')[0];
        segments = path.split('/').filter(Boolean);
        segment = segments.pop() || 'page';

        if (/^[0-9a-f-]{8,}$/i.test(segment) ||
            /^\d+$/.test(segment))
        {
            segment = segments.pop() || segment;
        }

        if (title)
        {
            result = title;
        }
        else
        {
            result = this.humanizeSegment(segment);
        }

        return result;
    }

    /**
     * @desc Read deepest activated route data.title when present
     * @access private
     * @param -
     * @return route title string or null
     */
    private readDeepestRouteTitle(): string | null
    {
        let route;
        let title;
        let result;

        route = this.router.routerState.root;
        title = null;

        while (route.firstChild)
        {
            route = route.firstChild;

            if (route.snapshot?.data?.['title'])
            {
                title = route.snapshot.data['title'];
            }
        }

        if (title)
        {
            result = String(title);
        }
        else
        {
            result = null;
        }

        return result;
    }

    /**
     * @desc Turn a URL segment into a short readable page name
     * @access private
     * @param segment : last non-id URL path segment
     * @return humanized page name
     */
    private humanizeSegment
    (
        segment: string
    ): string
    {
        let spaced;
        let result;

        spaced = (segment || 'page')
            .replace(/[-_]+/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .trim();
        result = spaced.replace(/\b\w/g, (ch) =>
        {
            return ch.toUpperCase();
        });

        return result;
    }

    /**
     * @desc   - initialize idle watcher and warning hooks
     * @access - private
     * @param  -
     * @return -
     */
    private configureIdleWatcher
    (
    )
    {
        this.idle.setIdle(this.idleStartSec);
        this.idle.setTimeout(this.timeoutPeriodSec);
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

        this.idleSubscriptions.add(
            this.idle.onIdleEnd.subscribe(() =>
            {
                this.idleState = 'No longer idle.';
                this.reset();
            })
        );

        this.idleSubscriptions.add(
            this.idle.onIdleStart.subscribe(() =>
            {
                this.idleState = 'You\'ve gone idle!';
                this.countdown = this.timeoutPeriodSec;

                if (!this.dialogRef)
                {
                    this.dialogRef = this.dialog.open(
                        IdleTimeoutComponent,
                        {
                            panelClass: 'modal-lg',
                            data:
                            {
                                idleObj: this.idle,
                                timeout: this.timeoutPeriodSec
                            }
                        }
                    );
                }
            })
        );

        this.idleSubscriptions.add(
            this.idle.onTimeoutWarning.subscribe((countdown: number) =>
            {
                this.idleState =
                    'You will time out in ' + countdown + ' seconds!';
                this.countdown = countdown;
            })
        );

        this.idleSubscriptions.add(
            this.idle.onInterrupt.subscribe(() =>
            {
                if (this.dialogRef)
                {
                    this.closeMe();
                    this.reset();
                }
            })
        );

        this.idleSubscriptions.add(
            this.idle.onTimeout.subscribe(() =>
            {
                this.idleState = 'Timed out!';
                this.timedOut = true;

                if (this.dialogRef)
                {
                    this.dialogRef.close();
                    this.dialogRef = null;
                }

                this.idle.stop();
                this.authService.logout(LOGOUT_REASON.IDLE);
            })
        );

        this.reset();
    }

    reset() 
    {
        this.idle.watch();
        this.idleState = 'Started.';
        this.timedOut = false;
    } 

    /**
     * @desc   - close the dialog
     * @access - public
     * @param  - 
     * @return - 
     */                
    closeMe() 
    {
        if (this.dialogRef)
        {
            this.dialogRef.close();
            this.dialogRef = null;
        }
    }    

/*
    
      hideChildModal(): void 
      {
        this.childModal.hide();
      }

      stay() {
        this.childModal.hide();
        this.reset();
      }

      logout() {
        this.childModal.hide();
//        this.appService.setUserLoggedIn(false);
        this.router.navigate(['/']);
      }   
*/ 
}

export { SecureLayoutComponent }